import { useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { nonNullable } from 'lib/array/arrayUtils'
import { isLEHotel } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { ManualItineraryDeleteRequest, TripItineraryData } from 'tripPlanner/api/types'
import { isMultiCartHotelItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import {
  computeCartPanelTotal,
  isCartPanelItemCheckoutReady,
  shouldIncludeCartPanelItemPriceEstimate,
  shouldShowReadyItemsOnlyNote,
} from '../utils/cartPanelUtils'
import { useCartPanelItems, type CartPanelAccommodationItem, type CartPanelFlightItem, type CartPanelItem } from './useCartPanelItems'
import { useCartPanelActions } from './useCartPanelActions'

export type { CartPanelItem }

interface UseCartPanelArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  chatId: number | undefined
  isStreaming: boolean
  sendMessage: (message: string) => Promise<void>
  deleteManualItineraryTarget: (target: ManualItineraryDeleteRequest) => Promise<void>
  isLoggedIn: boolean
}

interface UseCartPanelReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  isError: boolean
  totalPrice: number
  totalValue: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  loyaltyCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest>
  isLoggedIn: boolean
  handleToggleInclude: (item: CartPanelItem) => Promise<void>
  handleSetDates: () => void
  handleChooseTickets: (item: CartPanelItem) => void
  handleChooseDate: (item: CartPanelItem) => void
  handleChooseTime: (item: CartPanelItem) => void
  handleChooseDifferentTickets: (item: CartPanelItem) => void
  handleChooseFlight: (item: CartPanelFlightItem) => void
  handleRefreshFlight: (item: CartPanelFlightItem) => void
  handleChangeFlight: (item: CartPanelFlightItem) => void
  handleViewFlightDetails: (item: CartPanelFlightItem) => void
  refreshingFlightKey: string | undefined
  handleDelete: (item: CartPanelItem) => void
  handleCheckout: () => Promise<void>
  isCheckingOut: boolean
  refetch: () => void
  flightSearchDrawer: ReactNode
}

function getAccommodationLoyaltyCalculationRequest(
  item: CartPanelAccommodationItem,
  offers: Record<string, App.AnyOffer>,
  currentRegionCode: string,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (!item.offerId || !item.pricing.price) return undefined

  const rawOffer = offers[item.offerId]
  const offer = rawOffer && isLEHotel(rawOffer) ? rawOffer : undefined
  if (!offer) return undefined

  const cartHotelItem = item.cartItem && isMultiCartHotelItem(item.cartItem) ? item.cartItem : undefined
  const packageId = cartHotelItem?.packageId ?? item.packageId
  const roomRateId = cartHotelItem?.roomRateId ?? item.roomRateId
  const duration = item.duration ?? cartHotelItem?.duration
  if (!packageId || !roomRateId || !duration) return undefined

  const packageUniqueKey = getPackageUniqueKey(packageId, duration, roomRateId)
  const pkg = offer.packages.find(candidate => candidate.uniqueKey === packageUniqueKey)
  if (!pkg) return undefined

  return generateLoyaltyPointsCalculatorHotelOptions(offer, {
    pkg,
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice,
    currentRegionCode,
    nights: duration,
  })
}

function getItemLoyaltyCalculationRequest(
  item: CartPanelItem,
  offers: Record<string, App.AnyOffer>,
  currentRegionCode: string,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (item.cartExcluded || !shouldIncludeCartPanelItemPriceEstimate(item)) return undefined

  switch (item.productType) {
    case 'accommodation':
      return getAccommodationLoyaltyCalculationRequest(item, offers, currentRegionCode)
    case 'experience':
      if (!item.pricing.price) return undefined
      return {
        price: item.pricing.price,
        memberPrice: item.pricing.memberPrice ?? 0,
        offerId: item.experienceId,
        loyaltyProductType: 'experience',
      }
    case 'flight':
      return undefined
  }
}

export function useCartPanel({
  multiCartId,
  itineraryData,
  chatId,
  isStreaming,
  sendMessage,
  deleteManualItineraryTarget,
  isLoggedIn,
}: UseCartPanelArgs): UseCartPanelReturn {
  const [optimisticExclusions, setOptimisticExclusions] = useState<Record<number, boolean>>({})
  const [refreshFailedFlightKeys, setRefreshFailedFlightKeys] = useState<Record<string, boolean>>({})
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const offers = useAppSelector(getAllOffers)
  const { currentRegionCode } = useContext(GeoContext)

  const { items, isLoading, isError, refetch, refetchCartItems } = useCartPanelItems({
    multiCartId,
    itineraryData,
    isLoggedIn,
    optimisticExclusions,
    refreshFailedFlightKeys,
  })

  const actions = useCartPanelActions({
    chatId,
    multiCartId,
    items,
    isStreaming,
    isLoggedIn,
    sendMessage,
    deleteManualItineraryTarget,
    setOptimisticExclusions,
    setRefreshFailedFlightKeys,
    refetchCartItems,
  })

  const { totalPrice, totalValue, totalItemCount } = useMemo(
    () => computeCartPanelTotal(
      items.map(i => ({
        cartExcluded: i.cartExcluded,
        price: i.pricing.price,
        memberPrice: i.pricing.memberPrice,
        value: i.pricing.value,
        includeInTotal: shouldIncludeCartPanelItemPriceEstimate(i),
        includeInCount: true,
      })),
      hasLuxPlusBenefits,
    ),
    [items, hasLuxPlusBenefits],
  )

  const hasCheckoutReadyItems = useMemo(
    () => items.some(isCartPanelItemCheckoutReady),
    [items],
  )

  const showReadyItemsOnlyNote = useMemo(
    () => shouldShowReadyItemsOnlyNote(items),
    [items],
  )
  const loyaltyCalculationRequests = useMemo(
    () => nonNullable(items.map(item => getItemLoyaltyCalculationRequest(item, offers, currentRegionCode))),
    [items, offers, currentRegionCode],
  )

  return {
    items,
    isLoading,
    isError,
    totalPrice,
    totalValue,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    loyaltyCalculationRequests,
    isLoggedIn,
    ...actions,
    refetch,
  }
}
